import { mcaFormContextTitle } from "./mca.js";
export function getHeadingOrSubtitle({ formContextType, forms, mainFormTemplate, applicationTemplate, isHeading, admissionHasCompletedApplication, policeIncidentType, }) {
    if (formContextType === "admission") {
        const template = isHeading ? mainFormTemplate : applicationTemplate;
        if (template) {
            return `Section ${template.section} - ${admissionHasCompletedApplication ? "Admission" : "MHA Assessment"}`;
        }
        else {
            return admissionHasCompletedApplication ? "Admission" : "MHA Assessment";
        }
    }
    else if (formContextType === "police-incident") {
        // TODO: This is an assumpton which will not hold in the future.
        // It is currently the only type of form available from "police-incident"
        return policeIncidentType === "s136"
            ? "Section 136"
            : "Mental Capacity Act";
    }
    else if (formContextType === "soad-assessment") {
        // TODO: a fixed name, not derived from the forms present, so it cannot
        // distinguish a second kind of SOAD assessment if one is introduced.
        return "SOAD Assessment";
    }
    else if (formContextType === "mca-assessment") {
        return mcaFormContextTitle(forms);
    }
    else {
        return getFullFormName(mainFormTemplate);
    }
}
export function getFullFormName(template) {
    if (!template)
        return "";
    if (template.displayTitle)
        return template.displayTitle;
    const formName = template.category === "discharge"
        ? template.formName
        : `Form ${template.formName}`;
    if (template.section)
        return `${formName}: Section ${template?.section}${template?.description ? ` - ${template?.description}` : ""}`;
    // Form without a section:
    return `${formName}${template?.description ? `: ${template?.description}` : ""}`;
}
// This helps us find if a form has been merged or not, it will return null if it cannot find a merged form
export function findFormsMerged(mergedPatientData, formContext, form) {
    if (!mergedPatientData || !formContext)
        return null;
    const { patientId } = formContext;
    // find all the form context and/or forms that have been merged
    const hasBeenMerged = findMergedInfoIfFormBeforeLastMerge(mergedPatientData, formContext, patientId, form);
    // Check if forms belong to a different patient when no form is provided if true then the form would have been merged
    const isNotCurrentPatientForms = !form &&
        !formContext.forms.some((formEntry) => formEntry.patientId === patientId)
        ? mergedPatientData
        : null;
    // Check if the form belongs to a different patient when a form is provided if they are not the same then we assume that form has been merged
    const isDifferentPatientInForm = form && form.patientId !== patientId ? mergedPatientData : null;
    return hasBeenMerged || isNotCurrentPatientForms || isDifferentPatientInForm;
}
export function findMergedInfoIfFormBeforeLastMerge(mergedPatientData, formContext, patientId, form) {
    const hasFormBeenMerged = mergedPatientData?.previouslyMergedPatientsAndFormContextInfo?.find((mergedInfo) => mergedInfo.formContextId === formContext.id ||
        (form && mergedInfo.patientId !== patientId));
    // we assume the form has been merged if we can match the patient id to the hasFormBeenMerged patient id
    const isFormPatientIdMatchingLastMerge = form
        ? form.patientId === hasFormBeenMerged?.patientId
        : hasFormBeenMerged;
    return isFormPatientIdMatchingLastMerge ? hasFormBeenMerged : null;
}
/**
 * The author has declared that the supporting forms exist outside eMHA — the
 * "completed outside eMHA" toggle, plus a reason for it. A declaration
 * stands in place of a digital selection, so it is what excuses an incomplete
 * one.
 *
 * Only a part whose template permits a bypass — `linkableForms.isBypassable` —
 * can carry one, which is what scopes the declaration to the forms it is meant
 * for.
 */
export function supportingFormsBypassDeclared({ linkableForms, bypassToggleOn, notSelectedReason, }) {
    return !!linkableForms?.isBypassable && bypassToggleOn && !!notSelectedReason;
}
export function canLaunchForm({ linkableForms, supportingFormsProvided, notSelectedReason, validationOutcome, requiresSupportingForms, }) {
    // `supportingFormsProvided` is inverted: it is true when the bypass toggle is
    // ON, i.e. the forms were NOT all provided digitally.
    const isBypassed = supportingFormsBypassDeclared({
        linkableForms,
        bypassToggleOn: supportingFormsProvided,
        notSelectedReason,
    });
    const hasValidSelection = validationOutcome?.some((v) => v.type === "success") ||
        validationOutcome?.every((v) => v.type === "warning" && v.isIgnorable);
    // An error-level outcome must never be bypassable. The "completed outside
    // eMHA" toggle is only intended to excuse an incomplete digital selection
    // (a warning — e.g. one med rec linked, its partner completed on paper). It
    // must not let an invalid complete selection through, such as two med recs by
    // the same doctor, which produces an error.
    const hasError = validationOutcome?.some((v) => v.type === "error");
    return (!requiresSupportingForms || (!hasError && (isBypassed || hasValidSelection)));
}
// Intialise selected formTemplate state
export function initialiseForm(activeTemplates, formTemplateIdFromUrl) {
    const initialFormTemplate = activeTemplates.find((t) => t.id === formTemplateIdFromUrl) ??
        (activeTemplates.length === 1 ? activeTemplates[0] : undefined);
    const initialFormInputValue = initialFormTemplate?.longDescription ?? "";
    return { initialFormInputValue, initialFormTemplate };
}
// Pre-compute the "long description" for each form template + sort forms by name
export function preprocessTemplates(templates) {
    const numericCollator = new Intl.Collator([], { numeric: true });
    return templates
        .map((template) => ({
        ...template,
        longDescription: getFullFormName(template),
    }))
        .sort((a, b) => numericCollator.compare(a.formName, b.formName));
}
//# sourceMappingURL=form.js.map